import React, { useState, useEffect } from 'react';
import { Beaker, Book, Laptop, Video, Award, Users, Brain, ArrowRight, Check, Star, BarChart } from 'lucide-react';
import AdditionalComponentsExample from '../components/AdditionalComponent';

const AboutPage = () => {
  const [activeTab, setActiveTab] = useState('science');
  const [isVisible, setIsVisible] = useState(false);
  const [studentCount, setStudentCount] = useState(0);
  
  // Animation effects
  useEffect(() => {
    setIsVisible(true);
    
    // Animated counter for students
    const interval = setInterval(() => {
      setStudentCount(prev => {
        if (prev < 5000) return prev + 50;
        clearInterval(interval);
        return 5000;
      });
    }, 30);
    
    return () => clearInterval(interval);
  }, []);

  // Success stories data
  const testimonials = [
    { name: "Sanjila", achievement: "Secured 413 in Board I.Sc.", quote: "NAE's problem-solving approach transformed my understanding of Physics concepts." },
    { name: "Hazrat Ali", achievement: "Secured 403 in Board I.Arts", quote: "The recorded classes helped me revise complex topics multiple times until I mastered them." },
    { name: "Hasnain", achievement: "Secured 417 Board I.Arts", quote: "NAE's comprehensive Arts program gave me the edge I needed for my board exams." }
  ];

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* Hero Section with Brain Animation */}
      <div className="bg-gradient-to-r from-indigo-700 via-purple-700 to-blue-700 text-white py-24 relative overflow-hidden">
        <div className={`container mx-auto px-4 md:px-6 relative z-10 transition-all duration-1000 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Unlock Your <span className="text-yellow-300">Full Potential</span> with NAE Coaching
            </h1>
            <p className="text-xl md:text-2xl opacity-90 mb-8">
              Where knowledge meets innovation: Transform your learning journey with our cutting-edge education approach
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button className="bg-white text-indigo-700 px-8 py-3 rounded-full font-medium hover:bg-yellow-300 hover:text-indigo-800 transition-all transform hover:scale-105 shadow-lg">
                Start Your Journey
              </button>
              <button className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-full font-medium hover:bg-white hover:text-indigo-700 transition-all transform hover:scale-105">
                View Demo Class
              </button>
            </div>
          </div>
        </div>
        
        {/* Animated background elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-white"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                width: `${Math.random() * 100 + 50}px`,
                height: `${Math.random() * 100 + 50}px`,
                opacity: Math.random() * 0.5,
                animation: `float ${Math.random() * 10 + 10}s infinite ease-in-out`
              }}
            ></div>
          ))}
        </div>
      </div>

      {/* Brain-Stimulating Stats */}
      <div className="container mx-auto px-4 md:px-6 py-10">
        <div className="bg-white rounded-2xl shadow-xl p-8 -mt-20 relative z-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="text-center">
            <div className="text-4xl font-bold text-indigo-600 mb-2">{studentCount}+</div>
            <p className="text-gray-600">Success Stories</p>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-purple-600 mb-2">98%</div>
            <p className="text-gray-600">Pass Rate</p>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-blue-600 mb-2">24/7</div>
            <p className="text-gray-600">Learning Access</p>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-green-600 mb-2">500+</div>
            <p className="text-gray-600">Hours of Content</p>
          </div>
        </div>
      </div>

      {/* Interactive Course Tabs */}
      <div className="container mx-auto px-4 md:px-6 py-16">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Explore Our <span className="text-purple-600">Interactive</span> Programs</h2>
        
        <div className="mb-8 flex flex-wrap justify-center gap-4">
          <button 
            onClick={() => setActiveTab('science')}
            className={`px-6 py-3 rounded-full text-lg transition-all ${activeTab === 'science' ? 'bg-indigo-600 text-white shadow-lg' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            Science Stream
          </button>
          <button 
            onClick={() => setActiveTab('arts')}
            className={`px-6 py-3 rounded-full text-lg transition-all ${activeTab === 'arts' ? 'bg-purple-600 text-white shadow-lg' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            Arts Stream
          </button>
          <button 
            onClick={() => setActiveTab('foundation')}
            className={`px-6 py-3 rounded-full text-lg transition-all ${activeTab === 'foundation' ? 'bg-blue-600 text-white shadow-lg' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            Foundation (9-10)
          </button>
        </div>
        
        {/* Science Tab Content */}
        <div className={`transition-all duration-500 ${activeTab === 'science' ? 'opacity-100 max-h-screen' : 'opacity-0 max-h-0 overflow-hidden'}`}>
          <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-2xl p-8 shadow-lg">
            <div className="flex flex-col md:flex-row gap-8 items-center">
              <div className="w-full md:w-1/2">
                <h3 className="text-2xl font-bold text-indigo-800 mb-4">Science 11 & 12 with JEE/NEET Focus</h3>
                <p className="text-gray-700 mb-6">Our specialized science program is designed to build a strong conceptual foundation while preparing you for competitive exams like JEE and NEET.</p>
                
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="bg-indigo-100 p-2 rounded-full mr-4">
                      <Brain className="text-indigo-600 h-6 w-6" />
                    </div>
                    <div>
                      <h4 className="font-semibold">Concept Mastery</h4>
                      <p className="text-gray-600 text-sm">Deep understanding of fundamentals with practical applications</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="bg-indigo-100 p-2 rounded-full mr-4">
                      <BarChart className="text-indigo-600 h-6 w-6" />
                    </div>
                    <div>
                      <h4 className="font-semibold">Problem-Solving Techniques</h4>
                      <p className="text-gray-600 text-sm">Advanced strategies to tackle complex JEE and NEET problems</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="bg-indigo-100 p-2 rounded-full mr-4">
                      <Award className="text-indigo-600 h-6 w-6" />
                    </div>
                    <div>
                      <h4 className="font-semibold">Regular Mock Tests</h4>
                      <p className="text-gray-600 text-sm">Exam-like environment to build confidence and speed</p>
                    </div>
                  </div>
                </div>
                
                <button className="mt-8 bg-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-indigo-700 transition-all flex items-center gap-2 group">
                  Explore Science Courses
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
              
              <div className="w-full md:w-1/2 relative">
                <div className="bg-white rounded-xl shadow-lg p-4 transform rotate-3 hover:rotate-0 transition-all duration-300">
                  <img src="https://naendapi.naecoaching.com/media/images/sciencestudents.jpg" alt="Science Class" className="rounded-lg w-full" />
                  <div className="absolute -bottom-4 -right-4 bg-indigo-600 text-white text-sm py-2 px-4 rounded-full shadow-lg">
                    Live Digital Classes
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Arts Tab Content */}
        <div className={`transition-all duration-500 ${activeTab === 'arts' ? 'opacity-100 max-h-screen' : 'opacity-0 max-h-0 overflow-hidden'}`}>
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-8 shadow-lg">
            <div className="flex flex-col md:flex-row gap-8 items-center">
              <div className="w-full md:w-1/2">
                <h3 className="text-2xl font-bold text-purple-800 mb-4">Arts Class 11 & 12 Program</h3>
                <p className="text-gray-700 mb-6">Our comprehensive arts program nurtures creativity and critical thinking while ensuring academic excellence in all subjects.</p>
                
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="bg-purple-100 p-2 rounded-full mr-4">
                      <Book className="text-purple-600 h-6 w-6" />
                    </div>
                    <div>
                      <h4 className="font-semibold">Literature & Language Focus</h4>
                      <p className="text-gray-600 text-sm">In-depth analysis and appreciation of texts and linguistic concepts</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="bg-purple-100 p-2 rounded-full mr-4">
                      <Users className="text-purple-600 h-6 w-6" />
                    </div>
                    <div>
                      <h4 className="font-semibold">Social Sciences Expertise</h4>
                      <p className="text-gray-600 text-sm">Contemporary approach to history, political science, and sociology</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="bg-purple-100 p-2 rounded-full mr-4">
                      <Star className="text-purple-600 h-6 w-6" />
                    </div>
                    <div>
                      <h4 className="font-semibold">Creative Expression</h4>
                      <p className="text-gray-600 text-sm">Opportunities to develop writing and analytical skills</p>
                    </div>
                  </div>
                </div>
                
                <button className="mt-8 bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 transition-all flex items-center gap-2 group">
                  Discover Arts Program
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
              
              <div className="w-full md:w-1/2 relative">
                <div className="bg-white rounded-xl shadow-lg p-4 transform -rotate-3 hover:rotate-0 transition-all duration-300">
                  <img src="https://naendapi.naecoaching.com/media/images/atrs_students.jpeg" alt="Arts Class" className="rounded-lg w-full" />
                  <div className="absolute -bottom-4 -left-4 bg-purple-600 text-white text-sm py-2 px-4 rounded-full shadow-lg">
                    Interactive Sessions
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Foundation Tab Content */}
        <div className={`transition-all duration-500 ${activeTab === 'foundation' ? 'opacity-100 max-h-screen' : 'opacity-0 max-h-0 overflow-hidden'}`}>
          <div className="bg-gradient-to-r from-blue-50 to-teal-50 rounded-2xl p-8 shadow-lg">
            <div className="flex flex-col md:flex-row gap-8 items-center">
              <div className="w-full md:w-1/2">
                <h3 className="text-2xl font-bold text-blue-800 mb-4">Foundation Classes 9 & 10 (All Subjects)</h3>
                <p className="text-gray-700 mb-6">Build a strong academic foundation with our comprehensive program covering all subjects for Classes 9 and 10.</p>
                
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="bg-blue-100 p-2 rounded-full mr-4">
                      <Check className="text-blue-600 h-6 w-6" />
                    </div>
                    <div>
                      <h4 className="font-semibold">Complete Syllabus Coverage</h4>
                      <p className="text-gray-600 text-sm">All subjects taught by specialist teachers with board exam focus</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="bg-blue-100 p-2 rounded-full mr-4">
                      <Brain className="text-blue-600 h-6 w-6" />
                    </div>
                    <div>
                      <h4 className="font-semibold">Conceptual Clarity</h4>
                      <p className="text-gray-600 text-sm">Building understanding rather than memorization</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="bg-blue-100 p-2 rounded-full mr-4">
                      <Laptop className="text-blue-600 h-6 w-6" />
                    </div>
                    <div>
                      <h4 className="font-semibold">Digital Learning</h4>
                      <p className="text-gray-600 text-sm">Interactive digital board and engaging visual content</p>
                    </div>
                  </div>
                </div>
                
                <button className="mt-8 bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-all flex items-center gap-2 group">
                  View Foundation Courses
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
              
              <div className="w-full md:w-1/2 relative">
                <div className="bg-white rounded-xl shadow-lg p-4 transform rotate-2 hover:rotate-0 transition-all duration-300">
                  <img src="https://naendapi.naecoaching.com/media/images/foundation.jpg" alt="Foundation Class" className="rounded-lg w-full" />
                  <div className="absolute -bottom-4 -right-4 bg-blue-600 text-white text-sm py-2 px-4 rounded-full shadow-lg">
                    All Subjects Covered
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tech-Driven Learning Section */}
      <div className="bg-gradient-to-b from-gray-50 to-gray-100 py-20">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Revolutionary <span className="text-blue-600">Digital Learning</span> Experience</h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Our technology-driven approach transforms traditional teaching methods into interactive learning adventures
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow transform hover:-translate-y-1 duration-300">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto">
                <Laptop className="text-white h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-center mb-4">Interactive Digital Board</h3>
              <p className="text-gray-600 text-center">
                Experience concepts come alive through our interactive digital board technology that creates visual learning experiences
              </p>
              <div className="mt-6 text-center">
                <span className="inline-block bg-blue-100 text-blue-800 text-xs font-medium px-3 py-1 rounded-full">Visual Learning</span>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow transform hover:-translate-y-1 duration-300">
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto">
                <Video className="text-white h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-center mb-4">Recorded Classes</h3>
              <p className="text-gray-600 text-center">
                Access all classes anytime through our NAE App to review topics at your own pace and convenience
              </p>
              <div className="mt-6 text-center">
                <span className="inline-block bg-purple-100 text-purple-800 text-xs font-medium px-3 py-1 rounded-full">24/7 Access</span>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow transform hover:-translate-y-1 duration-300">
              <div className="bg-gradient-to-r from-green-500 to-green-600 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto">
                <Brain className="text-white h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-center mb-4">Brain-Stimulating Content</h3>
              <p className="text-gray-600 text-center">
                Engage with content designed to challenge your thinking and develop problem-solving abilities
              </p>
              <div className="mt-6 text-center">
                <span className="inline-block bg-green-100 text-green-800 text-xs font-medium px-3 py-1 rounded-full">Critical Thinking</span>
              </div>
            </div>
          </div>
          
          <div className="flex justify-center">
            <button className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-full font-medium hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg transform hover:scale-105 flex items-center gap-2">
              Experience Digital Learning
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Success Stories Carousel */}
      <div className="container mx-auto px-4 md:px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">Student Success Stories</h2>
        <p className="text-lg text-gray-700 text-center mb-12 max-w-3xl mx-auto">
          See how NAE Coaching transformed these students' academic journeys and helped them achieve their dreams
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow transform hover:-translate-y-1 duration-300 border border-gray-100">
              <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 h-3"></div>
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold">{testimonial.name}</h3>
                    <p className="text-indigo-600 text-sm">{testimonial.achievement}</p>
                  </div>
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                </div>
                <p className="text-gray-600 italic">"{testimonial.quote}"</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <AdditionalComponentsExample />

      {/* Brain-Stimulating Call to Action */}
      <div className="bg-gradient-to-r from-indigo-800 via-purple-800 to-blue-800 py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">Unlock Your Brain's Full Potential</h2>
          <p className="text-xl text-white/80 mb-10 max-w-3xl mx-auto">
            Join thousands of students who have transformed their learning experience and achieved academic excellence with NAE Coaching
          </p>
          
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <input 
                type="text" 
                placeholder="Your Name" 
                className="px-4 py-3 rounded-lg bg-white/20 text-white placeholder-white/60 border border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50"
              />
              <input 
                type="email" 
                placeholder="Email Address" 
                className="px-4 py-3 rounded-lg bg-white/20 text-white placeholder-white/60 border border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50"
              />
              <input 
                type="tel" 
                placeholder="Phone Number" 
                className="px-4 py-3 rounded-lg bg-white/20 text-white placeholder-white/60 border border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50"
              />
            </div>
            
            <button className="w-full bg-yellow-400 text-indigo-900 font-bold text-lg py-4 rounded-lg hover:bg-yellow-300 transition-colors shadow-lg flex items-center justify-center gap-2">
              Start Your Brain-Boosting Journey
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
          
          <p className="mt-6 text-white/60 text-sm">
            Join today and get access to a free demo class
          </p>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;